import clsx from "clsx";
import useFilteredTransactions from "../../hooks/useFilteredTransactions";
import React, { useEffect, useMemo } from "react";
import { formatCurrency } from "../../helpers/utils";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import useColors from "../../hooks/useColors";
import CommonTable from "../Tables/CommonTable";
import { ICellRendererParams } from "ag-grid-community";
import CommonBarLineChart from "../Charts/CommonBarLineChart";
import { ETransactionType } from "../../helpers/constants";
import useMainFilter from "../../hooks/useMainFilter";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import userPreferencesStore from "../../store/userPreferences";
import useCollapsibles from "../../hooks/useCollapsibles";
import useCharts from "../../hooks/useCharts";
import usePinnedColumns from "src/hooks/usePinnedColumns";
import useMobile from "src/hooks/useMobile";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { EReportsTableKeys } from "../Tables/tableIds";
import { EReportsChartKeys } from "../Charts/chartIds";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

interface IReportByMonth {
  onSelectionChange: (rows: Array<any>) => void;
  onDataChange: (data: any) => void;
}

const ReportByMonth: React.FC<IReportByMonth> = ({
  onSelectionChange,
  onDataChange,
}) => {
  const { isMobile } = useMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    collapsiblePreferences,
    updateCollapsible,
    toggleStatsMobileCollapsible,
    isStatsMobileCollapsibleOpen,
  } = useCollapsibles();
  const { chartPreferences } = useCharts();
  const { updatePinnedColumns, isPinnedColumn } = usePinnedColumns();
  const { colors } = useColors();
  const { currencySymbol } = useCurrentAccount();
  const { monthlyTransactionsSummary } = useFilteredTransactions();
  const { mainFilter, setMainFilter } = useMainFilter();
  const { selectedYear } = userPreferencesStore((state: any) => ({
    selectedYear: state.selectedYear,
  }));

  const columns: any = useMemo(
    () => [
      {
        field: "month",
        flex: 1,
        headerName: t("table_headings.month"),
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: "#555",
              color: "#fff",
              className: "text-right",
              onClick: () => {
                const firstDay = `${selectedYear}-${String(
                  params?.data?.month + 1
                ).padStart(2, "0")}-01`;
                const lastDay = moment(firstDay)
                  .endOf("month")
                  .format("yyyy-MM-DD");

                setMainFilter({
                  dateFrom: firstDay,
                  dateTo: lastDay,
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
        pinned: isPinnedColumn("monthlyReportMonthPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                monthlyReportMonthPinned: isPinned,
              });
            },
          },
        },
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.monthFormatted,
        // width: 150,
        // maxWidth: 150,
        resizable: true,
        minWidth: 150,
      },
      {
        field: "income",
        hide: mainFilter?.type === ETransactionType.EXPENSE,
        headerName: t("table_headings.income"),
        pinned: isPinnedColumn("monthlyReportIncomePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                monthlyReportIncomePinned: isPinned,
              });
            },
          },
        },
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.incomeFormatted,
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: colors.income.bg,
              color: colors.income.color,
              onClick: () => {
                const firstDay = `${selectedYear}-${String(
                  params?.data?.month + 1
                ).padStart(2, "0")}-01`;
                const lastDay = moment(firstDay)
                  .endOf("month")
                  .format("yyyy-MM-DD");

                setMainFilter({
                  dateFrom: firstDay,
                  dateTo: lastDay,
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
        suppressSizeToFit: true,
        minWidth: 170,
      },
      {
        field: "expenses",
        hide: mainFilter?.type === ETransactionType.INCOME,
        pinned: isPinnedColumn("monthlyReportExpensesPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                monthlyReportExpensesPinned: isPinned,
              });
            },
          },
        },
        headerName: t("table_headings.expenses"),
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.expensesFormatted,
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            params,
            custom: {
              background: colors.expenses.bg,
              color: colors.expenses.color,
              onClick: () => {
                const firstDay = `${selectedYear}-${String(
                  params?.data?.month + 1
                ).padStart(2, "0")}-01`;
                const lastDay = moment(firstDay)
                  .endOf("month")
                  .format("yyyy-MM-DD");

                setMainFilter({
                  dateFrom: firstDay,
                  dateTo: lastDay,
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
        suppressSizeToFit: true,
        minWidth: 170,
      },
      {
        hide: !!mainFilter?.type,
        field: "formattedDifference",
        headerName: t("table_headings.diff"),
        minWidth: 170,
        pinned: isPinnedColumn("monthlyReportDifferencePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                monthlyReportDifferencePinned: isPinned,
              });
            },
          },
        },
        valueGetter: (params: ICellRendererParams) =>
          params?.data?.differenceFormatted,
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const valueIsNegative = params?.data?.difference < 0;

          return {
            params,
            custom: {
              background: valueIsNegative
                ? colors.difference.negative.bg
                : colors.difference.positive.bg,
              color: valueIsNegative
                ? colors.difference.negative.color
                : colors.difference.positive.color,
              onClick: () => {
                const firstDay = `${selectedYear}-${String(
                  params?.data?.month + 1
                ).padStart(2, "0")}-01`;
                const lastDay = moment(firstDay)
                  .endOf("month")
                  .format("yyyy-MM-DD");

                setMainFilter({
                  dateFrom: firstDay,
                  dateTo: lastDay,
                });
                navigate(`/transactions/${mainFilter?.account}/`);
              },
            },
          };
        },
        suppressSizeToFit: true,
      },
    ],
    [colors, isPinnedColumn, selectedYear, t]
  );

  const months = useMemo(getMonths, []);

  const allValuesAreZero = useMemo(() => {
    return Object.values(monthlyTransactionsSummary).every((types) =>
      Object.values(types).every((total) => Number(total) === 0)
    );
  }, [monthlyTransactionsSummary]);

  const tableData = useMemo(
    () =>
      allValuesAreZero
        ? []
        : createTableData(
            months,
            monthlyTransactionsSummary,
            selectedYear,
            currencySymbol
          ),
    [months, monthlyTransactionsSummary, selectedYear, currencySymbol]
  );

  const seriesColors = useMemo(
    () => ({
      income: colors?.income?.bg,
      expenses: colors?.expenses?.bg,
    }),
    [colors?.income?.bg, colors?.expenses?.bg]
  );

  const chartData = useMemo(
    () =>
      createChartData(
        months,
        monthlyTransactionsSummary,
        selectedYear,
        seriesColors,
        mainFilter?.type,
        chartPreferences.type
      ),
    [
      months,
      monthlyTransactionsSummary,
      selectedYear,
      seriesColors,
      mainFilter?.type,
      chartPreferences.type,
    ]
  );

  useEffect(() => {
    onDataChange(tableData);
  }, [tableData]);

  return (
    <div className={clsx("flex w-full flex-col gap-4")}>
      <CollapsibleBlock
        title={t("common.chart")}
        open={collapsiblePreferences.statsChartOpen}
        onChange={(isOpen: boolean) => {
          updateCollapsible({ statsChartOpen: isOpen });
        }}
        uppercase
      >
        <CommonBarLineChart
          categories={months}
          series={chartData}
          chartId={EReportsChartKeys.MONTHLY_TRANSACTIONS}
        />
      </CollapsibleBlock>
      <CollapsibleBlock
        title={t("common.table")}
        open={collapsiblePreferences.statsTableOpen}
        onChange={(isOpen: boolean) => {
          updateCollapsible({ statsTableOpen: isOpen });
        }}
        uppercase
      >
        <CommonTable
          columns={columns}
          data={tableData}
          onSelectionChange={onSelectionChange}
          settingsKey={EReportsTableKeys.MONTHLY_TRANSACTIONS}
        />
      </CollapsibleBlock>
    </div>
  );
};

const getMonths = () => [
  i18next.t("months.january"),
  i18next.t("months.february"),
  i18next.t("months.march"),
  i18next.t("months.april"),
  i18next.t("months.may"),
  i18next.t("months.june"),
  i18next.t("months.july"),
  i18next.t("months.august"),
  i18next.t("months.september"),
  i18next.t("months.october"),
  i18next.t("months.november"),
  i18next.t("months.december"),
];

const createTableData = (
  months: string[],
  monthlyTransactionsSummary: any,
  selectedYear: string,
  currencySymbol: string
) => {
  return months.map((month, i) => {
    const key = selectedYear
      ? `${selectedYear}-${String(i + 1).padStart(2, "0")}`
      : `${String(i + 1).padStart(2, "0")}`;

    const income = monthlyTransactionsSummary[key]?.INCOME || 0;
    const expenses = monthlyTransactionsSummary[key]?.EXPENSE || 0;

    return {
      month: i,
      monthFormatted: month,
      income,
      incomeFormatted: formatCurrency({
        value: income,
        currency: currencySymbol,
      }),
      expenses,
      expensesFormatted: formatCurrency({
        value: expenses,
        currency: currencySymbol,
      }),
      difference: income - expenses,
      differenceFormatted: formatCurrency({
        value: income - expenses,
        currency: currencySymbol,
      }),
    };
  });
};

const createChartData = (
  months: string[],
  monthlyTransactionsSummary: any,
  selectedYear: string,
  seriesColors: any,
  type: string,
  chartType: string
) => {
  const data = [selectedYear].flatMap((year) => {
    const incomeData = months.map(
      (_, i) =>
        monthlyTransactionsSummary[
          year
            ? `${year}-${String(i + 1).padStart(2, "0")}`
            : `${String(i + 1).padStart(2, "0")}`
        ]?.INCOME || 0
    );

    const expensesData = months.map(
      (_, i) =>
        monthlyTransactionsSummary[
          year
            ? `${year}-${String(i + 1).padStart(2, "0")}`
            : `${String(i + 1).padStart(2, "0")}`
        ]?.EXPENSE || 0
    );

    return [
      {
        name: i18next.t("transaction_types.incomes"),
        data: incomeData,
        type: chartType,
        large: true,
        itemStyle: {
          color: seriesColors?.income,
          borderRadius: [6, 6, 0, 0],
        },
        show: type !== ETransactionType.EXPENSE,
      },
      {
        name: i18next.t("transaction_types.expenses"),
        data: expensesData,
        type: chartType,
        large: true,
        itemStyle: {
          color: seriesColors?.expenses,
          borderRadius: [6, 6, 0, 0],
        },
        show: type !== ETransactionType.INCOME,
      },
    ].filter(({ show }) => show);
  });

  return data;
};

export default React.memo(ReportByMonth);
